import "./comparison-table.scss";

import cn from "clsx";
import { Icon } from "design-system/components/icons/icon";
import { Alignment } from "design-system/types/types";
import { Theme } from "design-system/utils/theme";

export const ComparisonOptions = ["yes", "no", "some"] as const;
export type Comparison = (typeof ComparisonOptions)[number];

export interface Row {
  isFullRow?: boolean;
  rowHeader?: string;
  data?: Array<Comparison>;
}

export interface ComparisonTableProps {
  theme?: Theme;
  align?: Alignment;
  columnHeaders: Array<string>;
  rows: Array<Row>;
  showOnlyYesCells?: boolean;
}

export function ComparisonTable({
  theme,
  align,
  columnHeaders,
  rows,
  showOnlyYesCells,
}: ComparisonTableProps) {
  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component--comparison-table",
  );

  const renderComparison = (
    data: Comparison | undefined,
    i: number,
    columnHeader: string,
  ) => {
    switch (data) {
      case "yes":
        return (
          <td
            className="hbs-comparison-table__cell hbs-comparison-table__cell--yes"
            key={i}
            data-column-header={columnHeader}
          >
            <Icon
              name="checkCircle"
              className="hbs-comparison-table__icon"
              role="presentation"
            />{" "}
            Yes
          </td>
        );
      case "no":
        return (
          <td
            className="hbs-comparison-table__cell hbs-comparison-table__cell--no"
            key={i}
            data-column-header={columnHeader}
          >
            <Icon
              name="closeCircle"
              className="hbs-comparison-table__icon"
              role="presentation"
            />
            No
          </td>
        );

      case "some":
        return (
          <td
            className="hbs-comparison-table__cell"
            key={i}
            data-column-header={columnHeader}
          >
            <Icon
              name="slashCircle"
              className="hbs-comparison-table__icon"
              role="presentation"
            />
            Some
          </td>
        );
      default:
        break;
    }
  };

  return (
    <div className={wrapperClass}>
      <div data-theme={theme}>
        <table
          className={cn(
            "hbs-comparison-table",
            showOnlyYesCells && "hbs-comparison-table--show-yes",
          )}
        >
          <thead>
            <tr>
              {columnHeaders.map((header, i) => (
                <th key={i}>{i === 0 ? "" : header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className={cn(
                  row.isFullRow && "hbs-comparison-table__full-row",
                )}
              >
                <th data-column-header={columnHeaders[0]}>{row.rowHeader}</th>
                {row.data?.map((data, i) =>
                  renderComparison(data, i, columnHeaders[i + 1] ?? ""),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
